import { type QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'

import {
  confirmReceipt,
  confirmReceiptPlace,
  confirmReceiptVisit,
  createReceipt,
  deleteReceipt,
  getReceiptById,
  getReceiptPlaceCandidates,
  getReceipts,
  getReceiptsSummary,
  getReceiptVisitCandidates,
  setReceiptPlace,
  unconfirmReceiptPlace,
  updateReceipt,
  updateReceiptItem,
} from '@/api/services/receipt'
import type {
  CreateReceiptParams,
  GetReceiptsQuery,
  ReceiptDetail,
  ReceiptPlaceCandidate,
  UpdateReceiptItemParams,
  UpdateReceiptParams,
} from '@/api/types/receipt'
import { parseUtcDate } from '@/lib/date-utils'
import { DAY_DATA_QUERY_PREFIX } from '@/pages/activity/day-data'

/** 1回に取る件数。行が小さいので多めに取って読み込み回数を減らす */
export const RECEIPTS_PAGE_SIZE = 60

export const receiptKeys = {
  all: ['receipts'] as const,
  list: (query: GetReceiptsQuery) => ['receipts', 'list', query] as const,
  summary: (query: GetReceiptsQuery) => ['receipts', 'summary', query] as const,
  detail: (receiptId: number) => ['receipts', 'detail', receiptId] as const,
  visitCandidates: (receiptId: number) => ['receipts', 'visit-candidates', receiptId] as const,
  placeCandidates: (receiptId: number, q: string) => ['receipts', 'place-candidates', receiptId, q] as const,
}

export const useReceipts = (query: GetReceiptsQuery = {}, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: receiptKeys.list(query),
    queryFn: ({ signal }) => getReceipts(query, signal),
    enabled: options?.enabled ?? true,
  })

/** 一覧はページごとに取り足す。行はスクロール側で仮想化する。 */
export const useInfiniteReceipts = (query: GetReceiptsQuery = {}, options?: { enabled?: boolean }) =>
  useInfiniteQuery({
    queryKey: receiptKeys.list(query),
    queryFn: ({ pageParam, signal }) => getReceipts({ ...query, limit: RECEIPTS_PAGE_SIZE, offset: pageParam }, signal),
    initialPageParam: 0,
    // 満たない件数が返ってきたらそこが終端
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < RECEIPTS_PAGE_SIZE ? undefined : allPages.flat().length,
    enabled: options?.enabled ?? true,
  })

/** 合計と、絞り込みの選択肢に添える件数。 */
export const useReceiptsSummary = (query: GetReceiptsQuery = {}, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: receiptKeys.summary(query),
    queryFn: ({ signal }) => getReceiptsSummary(query, signal),
    enabled: options?.enabled ?? true,
  })

export const useReceipt = (receiptId: number | null) =>
  useQuery({
    queryKey: receiptKeys.detail(receiptId ?? 0),
    queryFn: ({ signal }) => getReceiptById(receiptId as number, signal),
    enabled: receiptId !== null,
  })

/** 一覧のバッジや件数も変わるので、更新系はまとめて receipts 配下を無効化する。 */
const useInvalidateReceipts = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: receiptKeys.all })
}

/**
 * 手で登録・削除したり購入日時を直したりしたら、レシート一覧に加えてタイムラインの支出も取り直す。
 * カード利用の「レシート未登録」も、レシートの有無で出たり消えたりする。
 * 無限タイムラインの日別データは日ごとに持っているので、関係した日の分だけ取り直す。
 */
const refreshSpendingViews = (queryClient: QueryClient, purchasedAts: (string | null | undefined)[]) => {
  void queryClient.invalidateQueries({ queryKey: ['card-transactions'] })
  const dates = new Set(purchasedAts.flatMap((iso) => (iso ? [format(parseUtcDate(iso), 'yyyy-MM-dd')] : [])))
  for (const date of dates) {
    void queryClient.invalidateQueries({ queryKey: [DAY_DATA_QUERY_PREFIX, date] })
  }
  return queryClient.invalidateQueries({ queryKey: receiptKeys.all })
}

export const useUpdateReceipt = (receiptId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: UpdateReceiptParams) => updateReceipt(receiptId, params),
    // 購入日時を直すと滞在が付け替わるので、直す前の日も取り直せるよう控えておく
    onMutate: () => ({
      previousPurchasedAt: queryClient.getQueryData<ReceiptDetail>(receiptKeys.detail(receiptId))?.purchased_at,
    }),
    onSuccess: (receipt, params, context) => {
      if (!('purchased_at' in params)) return queryClient.invalidateQueries({ queryKey: receiptKeys.all })
      return refreshSpendingViews(queryClient, [context?.previousPurchasedAt, receipt.purchased_at])
    },
  })
}

export const useUpdateReceiptItem = (receiptId: number) => {
  const invalidate = useInvalidateReceipts()
  return useMutation({
    mutationFn: ({ itemId, params }: { itemId: number; params: UpdateReceiptItemParams }) =>
      updateReceiptItem(receiptId, itemId, params),
    onSuccess: invalidate,
  })
}

export const useConfirmReceipt = () => {
  const invalidate = useInvalidateReceipts()
  return useMutation({
    mutationFn: (receiptId: number) => confirmReceipt(receiptId),
    onSuccess: invalidate,
  })
}

/** 手で登録する。 */
export const useCreateReceipt = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: CreateReceiptParams) => createReceipt(params),
    onSuccess: (receipt) => refreshSpendingViews(queryClient, [receipt.purchased_at]),
  })
}

export const useDeleteReceipt = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (receiptId: number) => deleteReceipt(receiptId),
    onSuccess: (_data, receiptId) => {
      const purchasedAt = queryClient.getQueryData<ReceiptDetail>(receiptKeys.detail(receiptId))?.purchased_at
      // 消したレシートの詳細を再取得すると 404 になるので、先にキャッシュから外す
      queryClient.removeQueries({ queryKey: receiptKeys.detail(receiptId) })
      return refreshSpendingViews(queryClient, [purchasedAt])
    },
  })
}

/** 場所の確定はレシートの中身の確定とは別軸で進む。 */
export const useConfirmReceiptPlace = (receiptId: number) => {
  const invalidate = useInvalidateReceipts()
  return useMutation({
    mutationFn: (placeId?: number) => confirmReceiptPlace(receiptId, placeId),
    onSuccess: invalidate,
  })
}

export const useUnconfirmReceiptPlace = (receiptId: number) => {
  const invalidate = useInvalidateReceipts()
  return useMutation({
    mutationFn: () => unconfirmReceiptPlace(receiptId),
    onSuccess: invalidate,
  })
}

/** 店名と場所名が一致せず自動では紐付かないレシートを、手で滞在に結びつけるための候補。 */
export const useReceiptVisitCandidates = (receiptId: number, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: receiptKeys.visitCandidates(receiptId),
    queryFn: ({ signal }) => getReceiptVisitCandidates(receiptId, signal),
    enabled: options?.enabled ?? true,
  })

export const useConfirmReceiptVisit = (receiptId: number) => {
  const invalidate = useInvalidateReceipts()
  return useMutation({
    mutationFn: (visitId: number) => confirmReceiptVisit(receiptId, visitId),
    onSuccess: invalidate,
  })
}

/** Google Places の候補。有料 API なので、開いている間だけ引いてしばらく保持する。 */
export const useReceiptPlaceCandidates = (receiptId: number, q: string, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: receiptKeys.placeCandidates(receiptId, q),
    queryFn: ({ signal }) => getReceiptPlaceCandidates(receiptId, q || undefined, signal),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  })

export const useSetReceiptPlace = (receiptId: number) => {
  const invalidate = useInvalidateReceipts()
  return useMutation({
    mutationFn: (candidate: ReceiptPlaceCandidate) => setReceiptPlace(receiptId, candidate),
    onSuccess: invalidate,
  })
}
